"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Sparkles, Search, ToggleLeft, ToggleRight, RefreshCw } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { cn } from "@/lib/utils";
import { Card, Spinner, Empty, useToast } from "@/components/ui";
import { TierChip } from "@/components/ui/ExamplePrimitives";

interface AdminTransitionRow {
  id: string;
  name: string;
  description: string | null;
  emoji: string | null;
  source: string;
  kind: "fm" | "css";
  tier: "FREE" | "PRO";
  isEnabled: boolean;
  cssDataT: string | null;
  cssDurationMs: number;
  framerVariants: unknown;
  sortOrder: number;
}

type TierFilter = "ALL" | "FREE" | "PRO";
type SourceFilter = "ALL" | "folder-1" | "folder-2";

/**
 * Admin: Page Transitions management.
 *
 * Lists every row in `ProposalTransition` (29 seeded by
 * `merryflix-backend/prisma/seed-transitions.ts`). Admins can:
 *  - flip each row between FREE and PRO
 *  - toggle each row's `isEnabled` flag (disabled rows are hidden
 *    from the picker and the viewer-side resolver falls back to a
 *    FREE row when the chosen id is disabled)
 *
 * The page is read-mostly — the only mutations are tier and isEnabled,
 * both PATCH `/admin/transitions/:id`. Sort order, framer variants,
 * and CSS data-t are seeded values and not editable here on purpose
 * (those change via a new seed + migration).
 */
export default function TransitionsAdminPage() {
  const [rows, setRows] = useState<AdminTransitionRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<TierFilter>("ALL");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("ALL");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.get<{ success: boolean; transitions: AdminTransitionRow[] }>(
        "/admin/transitions",
      );
      if (res.success) setRows(res.transitions);
    } catch (e: any) {
      // Surface the status code + message so admins can debug auth,
      // CORS, or "empty DB" scenarios without digging into the
      // network tab. Falls back to a generic message when neither is
      // available.
      const status = e?.status ? ` [${e.status}]` : "";
      const body = e?.message ?? "Unknown error";
      setError(`${body}${status} — did you run \`npm run seed:transitions\`?`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = useCallback(
    async (id: string, body: { tier?: "FREE" | "PRO"; isEnabled?: boolean }) => {
      setPendingId(id);
      try {
        const res = await adminApi.patch<{ success: boolean; transition: AdminTransitionRow }>(
          `/admin/transitions/${id}`,
          body,
        );
        if (res.success) {
          setRows((prev) =>
            prev?.map((r) => (r.id === id ? { ...r, ...body } : r)) ?? prev,
          );
          showToast({
            variant: "success",
            title: "Saved",
            description: `Updated ${res.transition.name}`,
          });
        }
      } catch (e: any) {
        showToast({
          variant: "error",
          title: "Save failed",
          description: e?.message ?? "Could not update transition",
        });
      } finally {
        setPendingId(null);
      }
    },
    [showToast],
  );

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => (tierFilter === "ALL" ? true : r.tier === tierFilter))
      .filter((r) => (sourceFilter === "ALL" ? true : r.source === sourceFilter))
      .filter((r) => {
        if (!q) return true;
        return (
          r.name.toLowerCase().includes(q) ||
          r.id.toLowerCase().includes(q) ||
          (r.description ?? "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [rows, search, tierFilter, sourceFilter]);

  const stats = useMemo(() => {
    if (!rows) return { total: 0, free: 0, pro: 0, enabled: 0 };
    return {
      total: rows.length,
      free: rows.filter((r) => r.tier === "FREE").length,
      pro: rows.filter((r) => r.tier === "PRO").length,
      enabled: rows.filter((r) => r.isEnabled).length,
    };
  }, [rows]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-pink-300/80">
            <Sparkles className="h-3.5 w-3.5" />
            Premium features
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">Page Transitions</h1>
          <p className="mt-1 max-w-xl text-sm text-zinc-400">
            Cinematic slide-to-slide transitions available in the proposal preview
            and public viewer. Toggle <span className="font-medium text-white/80">PRO</span>{" "}
            to gate a transition behind the subscription paywall, or disable a
            row to hide it without losing the in-flight references.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 transition hover:border-white/20 hover:bg-white/10"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </header>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Total" value={stats.total} />
        <StatTile label="FREE" value={stats.free} accent="green" />
        <StatTile label="PRO" value={stats.pro} accent="amber" />
        <StatTile label="Enabled" value={stats.enabled} accent="cyan" />
      </section>

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-white/[0.06] p-3">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 text-white/40" />
            <input
              type="search"
              placeholder="Search by name or id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-xs text-white placeholder:text-white/30 focus:outline-none"
            />
          </div>
          <FilterPills<TierFilter>
            value={tierFilter}
            onChange={setTierFilter}
            options={[
              { value: "ALL", label: "All tiers" },
              { value: "FREE", label: "FREE" },
              { value: "PRO", label: "PRO" },
            ]}
          />
          <FilterPills<SourceFilter>
            value={sourceFilter}
            onChange={setSourceFilter}
            options={[
              { value: "ALL", label: "All sources" },
              { value: "folder-1", label: "Folder 1" },
              { value: "folder-2", label: "Folder 2" },
            ]}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-sm text-white/50">
            <Spinner /> Loading transitions…
          </div>
        ) : error ? (
          <div className="p-12 text-center text-sm text-red-400">{error}</div>
        ) : !filtered.length ? (
          <Empty
            title="No transitions match"
            message="Try clearing the filters above."
          />
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {filtered.map((r) => (
              <TransitionRow
                key={r.id}
                row={r}
                pending={pendingId === r.id}
                onToggleTier={(tier) => patch(r.id, { tier })}
                onToggleEnabled={(isEnabled) => patch(r.id, { isEnabled })}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ─── subcomponents ───────────────────────────────────────────────────── */

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: "green" | "amber" | "cyan";
}) {
  const accentClass =
    accent === "green"
      ? "from-emerald-400/20 to-emerald-400/0 text-emerald-200"
      : accent === "amber"
        ? "from-amber-400/20 to-amber-400/0 text-amber-200"
        : accent === "cyan"
          ? "from-cyan-400/20 to-cyan-400/0 text-cyan-200"
          : "from-pink-400/20 to-pink-400/0 text-pink-200";
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-4">
      <div className={cn("absolute inset-0 bg-gradient-to-br opacity-50", accentClass)} />
      <div className="relative">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums text-white">{value}</div>
      </div>
    </div>
  );
}

function FilterPills<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-white/10 bg-black/30 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors",
            value === o.value
              ? "bg-white/10 text-white"
              : "text-white/55 hover:bg-white/[0.06] hover:text-white/80",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function TransitionRow({
  row,
  pending,
  onToggleTier,
  onToggleEnabled,
}: {
  row: AdminTransitionRow;
  pending: boolean;
  onToggleTier: (tier: "FREE" | "PRO") => void;
  onToggleEnabled: (isEnabled: boolean) => void;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-4 px-4 py-3 transition-colors",
        !row.isEnabled && "opacity-55",
        pending && "bg-white/[0.02]",
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-white/[0.06] to-white/[0.02] text-lg">
        {row.emoji ?? "•"}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white">{row.name}</span>
          <TierChip tier={row.tier} />
          <span
            className={cn(
              "rounded-full border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider",
              row.kind === "fm"
                ? "border-cyan-400/30 bg-cyan-400/10 text-cyan-200"
                : "border-fuchsia-400/30 bg-fuchsia-400/10 text-fuchsia-200",
            )}
          >
            {row.kind === "fm" ? "FM" : "CSS"}
          </span>
          <span className="rounded-full border border-white/10 bg-white/[0.03] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/55">
            {row.source}
          </span>
          {!row.isEnabled && (
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/55">
              disabled
            </span>
          )}
        </div>
        <p className="mt-1 line-clamp-1 text-xs text-white/55">{row.description}</p>
        <p className="mt-1 font-mono text-[10px] text-white/35">
          {row.id} · {row.cssDurationMs}ms
          {row.cssDataT ? ` · data-t=${row.cssDataT}` : ""}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => onToggleTier(row.tier === "PRO" ? "FREE" : "PRO")}
          disabled={pending}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-50",
            row.tier === "PRO"
              ? "border-amber-400/40 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20"
              : "border-emerald-400/40 bg-emerald-400/10 text-emerald-100 hover:bg-emerald-400/20",
          )}
          aria-label={`Set tier to ${row.tier === "PRO" ? "FREE" : "PRO"}`}
        >
          {row.tier === "PRO" ? "PRO" : "FREE"}
        </button>
        <button
          type="button"
          onClick={() => onToggleEnabled(!row.isEnabled)}
          disabled={pending}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-50",
            row.isEnabled
              ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-100 hover:bg-cyan-400/20"
              : "border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.06] hover:text-white/80",
          )}
          aria-label={row.isEnabled ? "Disable transition" : "Enable transition"}
        >
          {row.isEnabled ? (
            <>
              <ToggleRight className="h-3.5 w-3.5" />
              Enabled
            </>
          ) : (
            <>
              <ToggleLeft className="h-3.5 w-3.5" />
              Disabled
            </>
          )}
        </button>
      </div>
    </div>
  );
}
